import toast from 'react-hot-toast';
import styled from 'styled-components';
import { Roles } from 'shared/enums';
import { useWatch } from 'react-hook-form';

import { useForm } from '../../../../hooks';
import { Button, Input, Splash } from '../../../../shared/Components';
import { Box, FlexContainer, Text } from '../../../../shared/Typhography';
import {
  CollectionType,
  UploadCollectionField,
  uploadCollectionShema,
} from '../../../../validations/uploadCollection';
import { strings } from '../../../../strings';
import { addCollection, getAllCollections } from '../../../../services';
import { addColoringGridCollection } from '../../../../services/api/colloringGridCollectionApi';

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 20px;
`;

const AdminOnlyWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  & > div {
    width: 30px;
    max-width: 30px;
  }

  & > div > div {
    width: 30px;
    max-width: 30px;

    & > input {
      width: 30px;
      max-width: 30px;
    }
  }
`;

interface CollectionFormInputs {
  [UploadCollectionField.Type]: string;
  [UploadCollectionField.Name]: string;
  [UploadCollectionField.Keywords]: string;
  [UploadCollectionField.AdminOnly]: boolean;
}

function CreateCollectionScreen() {
  const { isLoading } = getAllCollections();
  const { mutate: editPuzzleCollection, isError: isErrorPuzzleCollection } = addCollection();
  const { mutate: editColoringGridCollection, isError: isErrorColoringGridCollection } =
    addColoringGridCollection();

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm<CollectionFormInputs>({
    schema: uploadCollectionShema,
    defaultValues: {
      [UploadCollectionField.Type]: 'puzzle',
      [UploadCollectionField.Name]: '',
      [UploadCollectionField.Keywords]: '',
      [UploadCollectionField.AdminOnly]: false,
    },
  });

  const formType: string = useWatch({ name: UploadCollectionField.Type, control: control });

  const submit = handleSubmit(async (dataInput: CollectionFormInputs) => {
    if (dataInput[UploadCollectionField.Type] === CollectionType.Puzzle) {
      editPuzzleCollection({
        keywords: dataInput.keywords.toUpperCase(),
        name: dataInput.name.trim(),
        numberOfPuzzles: 0,
        accessibleToRoles: dataInput.adminOnly ? [Roles.Admin] : [Roles.Artist, Roles.Player],
        isAdminOnly: Boolean(dataInput.adminOnly),
      });
    } else if (dataInput[UploadCollectionField.Type] === CollectionType.ColoringGrid) {
      editColoringGridCollection({
        name: dataInput.name.trim(),
        isAdminOnly: Boolean(dataInput.adminOnly),
        numberOfColoringGrids: 0,
      });
    }

    if (isErrorPuzzleCollection || isErrorColoringGridCollection) {
      reset();
      toast.error(strings.toastNotificationSomethingWrong);
    } else {
      toast.success(strings.toastNotificationSuccessCol);
    }
  });

  if (isLoading) {
    return <Splash />;
  }

  return (
    <Box margin="150px auto">
      <Text margin="20px 0" textAlign="center" size={2} weight="bold">
        {strings.createCollectionTitle}
      </Text>
      <FormWrapper>
        <FlexContainer flexDirection="row" flexWrap="wrap" justifyContent="space-between">
          <AdminOnlyWrapper>
            <Input
              type="radio"
              placeholder={strings.adminOnly}
              name={UploadCollectionField.Type}
              control={control}
              error={errors[UploadCollectionField.Type]}
              defaultValue="puzzle"
              width="30px"
              minWidth="30px"
              maxWidth="30px"
              marginBottom={10}
            />
            <Text style={{ marginBottom: 5 }} size={1}>
              Puzzle
            </Text>
          </AdminOnlyWrapper>

          <AdminOnlyWrapper>
            <Input
              type="radio"
              placeholder={strings.adminOnly}
              name={UploadCollectionField.Type}
              control={control}
              error={errors[UploadCollectionField.Type]}
              defaultValue="coloring-grid"
              width="30px"
              minWidth="30px"
              maxWidth="30px"
              marginBottom={10}
            />
            <Text style={{ marginBottom: 5 }} size={1}>
              Coloring grid
            </Text>
          </AdminOnlyWrapper>
        </FlexContainer>
        <Input
          required
          type="text"
          name={UploadCollectionField.Name}
          control={control}
          error={errors[UploadCollectionField.Name]}
          placeholder={strings.placeholderName}
          width="430px"
          mobWidth={100}
          marginBottom={10}
        />
        {formType === 'puzzle' && (
          <Input
            required
            type="text"
            placeholder={strings.placeholderKeywords}
            name={UploadCollectionField.Keywords}
            control={control}
            error={errors[UploadCollectionField.Keywords]}
            width="430px"
            mobWidth={100}
            marginBottom={10}
          />
        )}

        <AdminOnlyWrapper>
          <Input
            type="checkbox"
            placeholder={strings.adminOnly}
            name={UploadCollectionField.AdminOnly}
            control={control}
            error={errors[UploadCollectionField.AdminOnly]}
            width="30px"
            minWidth="30px"
            maxWidth="30px"
            marginBottom={10}
          />
          <Text style={{ marginBottom: 5 }} size={1}>
            {strings.adminOnly}
          </Text>
        </AdminOnlyWrapper>
        <Button
          style={{ marginLeft: 'auto', marginRight: 0 }}
          width={200}
          height={50}
          type="submit"
          onClick={submit}
          margin="auto"
        >
          {strings.submit}
        </Button>
      </FormWrapper>
    </Box>
  );
}

export default CreateCollectionScreen;
